/**
 * The PRODUCTION config — what the engine and the device host read (PLAN
 * §7.1a, D22, D32): devices, mappings, source/output layers, piece types,
 * macro actions, adlib panels and settings, as one document. Since D32 nobody
 * writes it by hand: it is `resolveProductionConfig(showStyle, studio)`
 * (`resolveConfig.ts`) of a show style (`showStyle.ts`: piece types, layers,
 * panels, actions) and a studio (`studio.ts`: devices, mappings, settings). A
 * production runs from a **snapshot** of it taken at launch.
 *
 * **These zod schemas are a stable export and this document's shape is
 * frozen.** A production created before D32 holds a snapshot of the old
 * studio config, which is exactly this shape, and a pinned production process
 * parses the snapshot it is handed with ITS copy of this schema (PLAN §4.3,
 * §6.8): a change here is additive only. The show style and studio schemas
 * reuse the parts below. The exported names:
 *
 *   productionConfigSchema  the whole config, cross-references checked
 *   deviceConfigSchema      devices[id]            { type, standbyPolicy, options }
 *   mappingSchema           mappings[layer]        { device, lookahead, lookaheadDepth, lookaheadMaxSearchDistance, options }
 *   lookaheadModeSchema     'none' | 'preload' | 'when-clear'
 *   sourceLayerSchema       sourceLayers[id]       { label }
 *   outputLayerSchema       outputLayers[id]       { label, isPgm }
 *   paramSpecSchema         one piece-type param   (a small JSON-schema subset)
 *   pieceTypeSchema         pieceTypes[id]
 *   pieceTypeTimelineEntrySchema  one timeline-object template of a piece type
 *   actionSchema / actionStepSchema   actions[id]  { label, steps: [fire | stop] }
 *   adlibPanelSchema        adlibPanels[]          { id, label, items }
 *   studioSettingsSchema    settings               { minimumTakeSpanMs }
 *   configIdSchema / layerIdSchema / layerPatternSchema   the id formats
 *   definitionProblems / mappingProblems / withOneShot    the checks and the fill the show style and studio share
 *
 * Every object is `.strict()`: an unknown key in a stored config is a typo or a
 * version skew, and a silently dropped mapping option is a device that does
 * the wrong thing on air.
 */
import { durationMsSchema, jsonValueSchema } from '@acp/domain/primitives.js';
import { pieceLifespanSchema } from '@acp/domain/fields.js';
import { z } from 'zod';

/** A config key: a device id, a piece-type id, an action id, a param name. Dots allowed (`lower.third`). */
export const configIdSchema = z.string().regex(/^[A-Za-z0-9_][A-Za-z0-9_.:-]{0,127}$/, {
  message: 'not an id [A-Za-z0-9_.:-]{1,128} starting with a letter, digit or _',
});

/**
 * A layer id — a mapping, source or output layer. Letters, digits and `_`
 * only: layers are timeline layers, and `superfly-timeline` reserves `.`,
 * `#`, `$` and its expression operators (`+ - * / % & | ! ( )`) in layer
 * names, ids and classes. (PLAN §7.1a's sketch writes `vmx.pgm`; here that
 * is `vmx_pgm`.)
 */
export const layerIdSchema = z.string().regex(/^[A-Za-z0-9_]{1,128}$/, { message: 'not a layer id [A-Za-z0-9_]{1,128}' });

/** A layer pattern for `stop` steps: a source-layer id where `*` matches any run of characters (`gfx_*`). */
export const layerPatternSchema = z.string().regex(/^[A-Za-z0-9_*]{1,128}$/, { message: 'not a layer pattern [A-Za-z0-9_*]' });

/** A timeline class name (superfly-timeline `.class` references; the same reserved characters). */
const classNameSchema = z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/, { message: 'not a class name' });

const openOptionsSchema = z.record(z.string(), jsonValueSchema);

/**
 * D8: what a device does while its node is the standby. `observe` connects and
 * reads status; `disconnected` stays off the device until promotion.
 */
export const standbyPolicySchema = z.enum(['observe', 'disconnected']);

/**
 * The drivers a device can be (`packages/devices` maps each to a TSR
 * integration): the three v1 simulated devices (PLAN §7.3) and the
 * internet-reachable ones of cloud productions (D10). Additive: a new driver
 * is a new name here.
 */
export const DEVICE_TYPES = ['sim-vision-mixer', 'sim-video-server', 'sim-graphics', 'vmix', 'obs', 'httpSend'] as const;
export const deviceTypeSchema = z.enum(DEVICE_TYPES);
export type DeviceTypeName = z.infer<typeof deviceTypeSchema>;

/**
 * The `content.deviceType` a timeline object for each driver carries — TSR
 * routes an object to a device only when it matches. The simulated devices'
 * are theirs (`packages/devices`); the others are TSR's `DeviceType` values.
 * The timeline generator fills it in from the object's mapping unless the
 * piece type's template sets one.
 */
export const TIMELINE_DEVICE_TYPE: Readonly<Record<DeviceTypeName, string>> = {
  'sim-vision-mixer': 'ACP_SIM_MIXER',
  'sim-video-server': 'ACP_SIM_VIDEO',
  'sim-graphics': 'ACP_SIM_GRAPHICS',
  vmix: 'VMIX',
  obs: 'OBS',
  httpSend: 'HTTPSEND',
};

/** Drivers whose mappings are one-shot unless the mapping says otherwise (D8: clip playback is not re-fired). */
export const ONE_SHOT_BY_DEFAULT: readonly DeviceTypeName[] = ['sim-video-server'];

export const deviceConfigSchema = z
  .object({
    /** The driver (`DEVICE_TYPES`). */
    type: deviceTypeSchema,
    standbyPolicy: standbyPolicySchema.default('observe'),
    /** Driver-specific connection options (host, port, …), passed to the driver untouched. */
    options: openOptionsSchema.default({}),
  })
  .strict();

/** Sofie's `LookaheadMode` (PLAN §7.1: "Lookahead (PRELOAD / WHEN_CLEAR) is configured per mapping"). */
export const lookaheadModeSchema = z.enum(['none', 'preload', 'when-clear']);
export type LookaheadMode = z.infer<typeof lookaheadModeSchema>;

/** Sofie's default search distance (`lookaheadMaxSearchDistance`), in parts. */
export const DEFAULT_LOOKAHEAD_SEARCH_DISTANCE = 10;

export const mappingSchema = z
  .object({
    /** The device (a key of `devices`) this layer's objects go to. */
    device: configIdSchema,
    lookahead: lookaheadModeSchema.default('none'),
    /** How many future objects to look ahead for (Sofie's `lookaheadDepth`). */
    lookaheadDepth: z.number().int().min(1).max(4).default(1),
    /** How many future parts to search (Sofie's `lookaheadMaxSearchDistance`). */
    lookaheadMaxSearchDistance: z.number().int().min(1).max(50).default(DEFAULT_LOOKAHEAD_SEARCH_DISTANCE),
    /**
     * D8: a one-shot layer (clip playback) is never re-fired by a promotion
     * resync; it needs an operator's confirmation instead. Unset: by the
     * device's driver (`ONE_SHOT_BY_DEFAULT`) — `productionConfigSchema` fills it,
     * so a parsed config always has a boolean here.
     */
    oneShot: z.boolean().optional(),
    /**
     * Device-specific mapping options, passed to TSR untouched (the simulated
     * devices': mixer `{mappingType:'me', me}` | `{mappingType:'keyer', me,
     * keyer}` | `{mappingType:'aux', aux}`; video `{mappingType:'channel',
     * channel}`; graphics `{mappingType:'layer', layer}` — all 0-based).
     */
    options: openOptionsSchema.default({}),
  })
  .strict();

export const sourceLayerSchema = z.object({ label: z.string().max(200).default('') }).strict();

export const outputLayerSchema = z
  .object({ label: z.string().max(200).default(''), isPgm: z.boolean().default(false) })
  .strict();

/**
 * One piece-type parameter: the JSON-schema subset the SPA can build a form
 * from and the engine can check a value against. `required` params must be
 * present (a `default` counts as present).
 */
export const paramSpecSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('string'),
      label: z.string().max(200).default(''),
      required: z.boolean().default(false),
      default: z.string().nullable().default(null),
      enum: z.array(z.string()).nullable().default(null),
      maxLength: z.number().int().positive().nullable().default(null),
    })
    .strict(),
  z
    .object({
      type: z.enum(['number', 'integer']),
      label: z.string().max(200).default(''),
      required: z.boolean().default(false),
      default: z.number().nullable().default(null),
      minimum: z.number().nullable().default(null),
      maximum: z.number().nullable().default(null),
    })
    .strict(),
  z
    .object({
      type: z.literal('boolean'),
      label: z.string().max(200).default(''),
      required: z.boolean().default(false),
      default: z.boolean().nullable().default(null),
    })
    .strict(),
]);
export type ParamSpec = z.infer<typeof paramSpecSchema>;

/** A timing window relative to the piece's start (a template entry, a keyframe). */
const relativeEnableSchema = z
  .object({ start: durationMsSchema.default(0), duration: durationMsSchema.nullable().default(null) })
  .strict();

export const pieceTypeKeyframeSchema = z
  .object({
    enable: relativeEnableSchema,
    /** Merged into the object's content while the keyframe is active; `{{param}}` substituted. */
    content: openOptionsSchema,
  })
  .strict();

/**
 * One timeline object a piece of this type produces: its mapping layer, a
 * content template (`{{param}}` substituted — a string that is exactly
 * `{{name}}` becomes the param's typed value), and its window within the piece.
 */
export const pieceTypeTimelineEntrySchema = z
  .object({
    layer: layerIdSchema,
    content: openOptionsSchema,
    enable: relativeEnableSchema.default({ start: 0, duration: null }),
    classes: z.array(classNameSchema).default([]),
    keyframes: z.array(pieceTypeKeyframeSchema).default([]),
  })
  .strict();
export type PieceTypeTimelineEntry = z.infer<typeof pieceTypeTimelineEntrySchema>;

export const pieceTypeSchema = z
  .object({
    label: z.string().max(200).default(''),
    params: z.record(configIdSchema, paramSpecSchema).default({}),
    sourceLayer: layerIdSchema,
    outputLayer: layerIdSchema.nullable().default(null),
    lifespan: pieceLifespanSchema.default('part-only'),
    /** How long a piece of this type runs when its row does not say; null = until something ends it. */
    defaultDuration: durationMsSchema.nullable().default(null),
    prerollDuration: durationMsSchema.default(0),
    postrollDuration: durationMsSchema.default(0),
    /** No objects = a virtual type (a marker that only ends an infinite). */
    timeline: z.array(pieceTypeTimelineEntrySchema).default([]),
  })
  .strict();
export type PieceTypeConfig = z.infer<typeof pieceTypeSchema>;

const paramsSchema = z.record(z.string(), jsonValueSchema);

/** Fire a piece type (PLAN §7.1a: `{pieceType:'vt', params:{clip:'break'}}`). */
export const fireStepSchema = z
  .object({
    pieceType: configIdSchema,
    params: paramsSchema.default({}),
    mode: z.enum(['now', 'queue']).default('now'),
  })
  .strict();

/** Stop whatever plays on the matching source layers (PLAN §7.1a: `{stop:'gfx.*'}`). */
export const stopStepSchema = z.object({ stop: layerPatternSchema }).strict();

export const actionStepSchema = z.union([fireStepSchema, stopStepSchema]);
export type ActionStep = z.infer<typeof actionStepSchema>;

export const actionSchema = z
  .object({ label: z.string().max(200).default(''), steps: z.array(actionStepSchema).min(1).max(32) })
  .strict();
export type ActionConfig = z.infer<typeof actionSchema>;

const hotkeySchema = z.string().max(64).nullable().default(null);

export const adlibPanelItemSchema = z.union([
  z
    .object({
      label: z.string().max(200),
      pieceType: configIdSchema,
      params: paramsSchema.default({}),
      mode: z.enum(['now', 'queue']).default('now'),
      hotkey: hotkeySchema,
    })
    .strict(),
  z.object({ label: z.string().max(200), action: configIdSchema, hotkey: hotkeySchema }).strict(),
]);

export const adlibPanelSchema = z
  .object({ id: configIdSchema, label: z.string().max(200).default(''), items: z.array(adlibPanelItemSchema).default([]) })
  .strict();

/** Sofie's `minimumTakeSpan` default (PLAN §5.3: "`minimumTakeSpan` (default 1 s)"). */
export const DEFAULT_MINIMUM_TAKE_SPAN_MS = 1000;

export const studioSettingsSchema = z
  .object({ minimumTakeSpanMs: durationMsSchema.default(DEFAULT_MINIMUM_TAKE_SPAN_MS) })
  .strict();

const productionConfigObjectSchema = z
  .object({
    devices: z.record(configIdSchema, deviceConfigSchema).default({}),
    mappings: z.record(layerIdSchema, mappingSchema).default({}),
    sourceLayers: z.record(layerIdSchema, sourceLayerSchema).default({}),
    outputLayers: z.record(layerIdSchema, outputLayerSchema).default({}),
    pieceTypes: z.record(configIdSchema, pieceTypeSchema).default({}),
    actions: z.record(configIdSchema, actionSchema).default({}),
    adlibPanels: z.array(adlibPanelSchema).default([]),
    settings: studioSettingsSchema.default({ minimumTakeSpanMs: DEFAULT_MINIMUM_TAKE_SPAN_MS }),
  })
  .strict();

type ProductionConfigShape = z.infer<typeof productionConfigObjectSchema>;

/** A config problem: where (a path into the document) and what. */
export type ConfigProblem = { readonly path: (string | number)[]; readonly message: string };

/** What an operator plays — layers, piece types, actions, panels: a show style's part of the config (D32). */
export type Definitions = Pick<ProductionConfigShape, 'sourceLayers' | 'outputLayers' | 'pieceTypes' | 'actions' | 'adlibPanels'>;

/**
 * Every cross-reference the definitions make: a piece type names an existing
 * source/output layer and existing timeline layers (keys of `timelineLayers`:
 * the `mappings` here, a show style's `layers` — `noun` names them in the
 * message), an action or panel item an existing piece type or action.
 */
export function definitionProblems(config: Definitions, timelineLayers: Readonly<Record<string, unknown>>, noun: string): ConfigProblem[] {
  const problems: ConfigProblem[] = [];
  for (const [id, type] of Object.entries(config.pieceTypes)) problems.push(...pieceTypeProblems(config, id, type, timelineLayers, noun));
  for (const [id, action] of Object.entries(config.actions)) {
    action.steps.forEach((step, index) => {
      if ('pieceType' in step && !Object.hasOwn(config.pieceTypes, step.pieceType)) {
        problems.push({ path: ['actions', id, 'steps', index, 'pieceType'], message: `unknown piece type "${step.pieceType}"` });
      }
    });
  }
  config.adlibPanels.forEach((panel, panelIndex) => {
    panel.items.forEach((item, itemIndex) => {
      const path = ['adlibPanels', panelIndex, 'items', itemIndex];
      if ('pieceType' in item && !Object.hasOwn(config.pieceTypes, item.pieceType)) {
        problems.push({ path: [...path, 'pieceType'], message: `unknown piece type "${item.pieceType}"` });
      }
      if ('action' in item && !Object.hasOwn(config.actions, item.action)) {
        problems.push({ path: [...path, 'action'], message: `unknown action "${item.action}"` });
      }
    });
  });
  return problems;
}

/** Every mapping names an existing device (a studio's check, D32). */
export function mappingProblems(config: Pick<ProductionConfigShape, 'devices' | 'mappings'>): ConfigProblem[] {
  const problems: ConfigProblem[] = [];
  for (const [layer, mapping] of Object.entries(config.mappings)) {
    if (!Object.hasOwn(config.devices, mapping.device)) {
      problems.push({ path: ['mappings', layer, 'device'], message: `unknown device "${mapping.device}"` });
    }
  }
  return problems;
}

/** Every cross-reference the config makes, as `[path, problem]` pairs. Empty = consistent. */
export function productionConfigProblems(config: ProductionConfigShape): ConfigProblem[] {
  return [...mappingProblems(config), ...definitionProblems(config, config.mappings, 'mapping')];
}

function pieceTypeProblems(config: Definitions, id: string, type: PieceTypeConfig, timelineLayers: Readonly<Record<string, unknown>>, noun: string): ConfigProblem[] {
  const problems: ConfigProblem[] = [];
  if (!Object.hasOwn(config.sourceLayers, type.sourceLayer)) {
    problems.push({ path: ['pieceTypes', id, 'sourceLayer'], message: `unknown source layer "${type.sourceLayer}"` });
  }
  if (type.outputLayer !== null && !Object.hasOwn(config.outputLayers, type.outputLayer)) {
    problems.push({ path: ['pieceTypes', id, 'outputLayer'], message: `unknown output layer "${type.outputLayer}"` });
  }
  type.timeline.forEach((entry, index) => {
    if (!Object.hasOwn(timelineLayers, entry.layer)) {
      problems.push({ path: ['pieceTypes', id, 'timeline', index, 'layer'], message: `unknown ${noun} "${entry.layer}"` });
    }
  });
  return problems;
}

/**
 * The whole production config, with every cross-reference checked: a mapping
 * names an existing device, a piece type an existing source/output layer and
 * mappings, an action or panel item an existing piece type or action.
 */
export const productionConfigSchema = productionConfigObjectSchema
  .superRefine((config, ctx) => {
    for (const problem of productionConfigProblems(config)) ctx.addIssue({ code: 'custom', path: problem.path, message: problem.message });
  })
  .transform((config) => ({ ...config, mappings: withOneShot(config) }));

export type MappingConfig = Omit<z.output<typeof mappingSchema>, 'oneShot'> & { readonly oneShot: boolean };

/** Every mapping's `oneShot` resolved: as written, else by its device's driver. */
export function withOneShot(config: Pick<ProductionConfigShape, 'devices' | 'mappings'>): Record<string, MappingConfig> {
  const out: Record<string, MappingConfig> = {};
  for (const [layer, mapping] of Object.entries(config.mappings)) {
    const type = config.devices[mapping.device]?.type;
    const oneShot = mapping.oneShot ?? (type !== undefined && ONE_SHOT_BY_DEFAULT.includes(type));
    out[layer] = { ...mapping, oneShot };
  }
  return out;
}


export type ProductionConfig = z.output<typeof productionConfigSchema>;
/** What a caller may write: defaults not yet applied. */
export type ProductionConfigInput = z.input<typeof productionConfigSchema>;

export type ParseProductionConfigResult =
  | { readonly ok: true; readonly config: ProductionConfig }
  | { readonly ok: false; readonly error: string };

/** Parse and cross-check untrusted config input. Never throws. */
export function parseProductionConfig(input: unknown): ParseProductionConfigResult {
  const result = productionConfigSchema.safeParse(input);
  return result.success ? { ok: true, config: result.data } : { ok: false, error: z.prettifyError(result.error) };
}
